import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Award, Users, Leaf } from 'lucide-react';

const WhyUsPage = () => {
  const reasons = [
    {
      icon: Shield,
      title: 'Honest Advice',
      description: "We tell you what's actually wrong and what genuinely needs fixing. No upselling, no unnecessary work.",
    },
    {
      icon: Award,
      title: 'Proper Equipment',
      description: 'We invest in professional diagnostic and service equipment. The right tools make accurate diagnosis possible.',
    },
    {
      icon: Users,
      title: 'Experienced Technicians',
      description: 'Our technicians understand vehicle air conditioning systems inside and out.',
    },
    {
      icon: Leaf,
      title: 'Environmentally Responsible',
      description: 'We handle refrigerants properly and follow all environmental regulations for recovery and disposal.',
    },
  ];

  const commitments = [
    'We check your system before recommending any work',
    'We explain what we find in plain English',
    'We provide written quotes before starting',
    'We only carry out work you approve',
    'We use quality parts with warranties',
    'We stand behind our work',
  ];

  return (
    <div data-testid="why-us-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
              About Us
            </span>
            <h1 className="text-4xl font-bold text-slate-900 mt-3 mb-6">
              Why Choose Us
            </h1>
            <p className="text-lg text-slate-600">
              We take a different approach to vehicle air conditioning. Instead of "top up and hope", we diagnose properly and repair correctly. It's what we'd want for our own vehicles.
            </p>
          </div>
        </div>
      </section>

      {/* Reasons Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {reasons.map((reason) => (
              <div key={reason.title} className="flex gap-4 p-6 bg-slate-50 rounded-xl">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <reason.icon className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-slate-600">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Our Approach
              </h2>
              <p className="text-slate-600 mb-6">
                Vehicle air conditioning isn't complicated, but it does require proper diagnosis. Too many garages just add refrigerant without checking for leaks or other faults. This approach wastes money and harms the environment.
              </p>
              <p className="text-slate-600 mb-6">
                We do it properly. Every system is checked before work is recommended. If there's a leak, we find it. If there's a fault, we diagnose it. Then we explain what we've found and give you options.
              </p>
              <p className="text-slate-600">
                This honest approach means you only pay for work that's actually needed, and repairs that actually last.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=600&q=80"
                alt="Professional vehicle service"
                className="rounded-xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitments */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">
              Our Commitments
            </h2>
            <ul className="space-y-4">
              {commitments.map((commitment) => (
                <li key={commitment} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{commitment}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Serving Eastbourne & East Sussex
            </h2>
            <p className="text-slate-600 mb-4">
              We're based in Eastbourne but serve customers throughout East Sussex. Whether you're in Eastbourne, Polegate, Hailsham, Seaford, or the surrounding areas, we're here to help.
            </p>
            <p className="text-slate-600">
              <strong>Address:</strong> Unit 2, Hawthorn Road, Eastbourne BN23 6QA
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-slate-300 mb-8">
            Get in touch for honest advice about your vehicle's air conditioning.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            data-testid="why-us-cta-contact"
          >
            Contact Us
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyUsPage;
